import * as React from 'react';
import { Row } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type AssociatedSubjobVM from 'ab-viewModels/workRequest/associatedSubjob.viewModel';

import { filterMap } from '@acceligentllc/shared/utils/array';

import { Button, SearchInput } from '@acceligentllc/storybook';

import Radio from 'af-components/Controls/Radio';
import CustomModal from 'af-components/CustomModal';
import Label from 'af-components/Label';
import LoadingIndicator from 'af-components/LoadingIndicator';
import SubmitButton from 'af-components/SubmitButton';

import * as WorkOrderActions from 'af-actions/workOrder';

import styles from './styles.module.scss';

const MODAL_TITLE = 'Export Associated Subjob Data';

interface OwnProps {
	closeModal: () => void;
	onSubmit: (workRequestId: number, workRequestCode: string) => void;
	showModal: boolean;
	workOrderId: Nullable<number>;
	forCustomer: boolean;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const VirtualReportExportModal: React.FC<Props> = (props) => {
	const {
		showModal,
		closeModal,
		findSubjobs,
		onSubmit,
		workOrderId,
		forCustomer,
	} = props;

	const [associatedSubjobs, setAssociatedSubjobs] = React.useState<Nullable<AssociatedSubjobVM[]>>(null);
	const [selectedSubjobId, setSelectedSubjobId] = React.useState<Nullable<number>>(null);
	const [searchText, setSearchText] = React.useState<string>('');

	const fetchData = React.useCallback(async () => {
		if (!workOrderId) {
			return;
		}
		const subjobs = await findSubjobs(workOrderId);

		setAssociatedSubjobs(subjobs);
	}, [findSubjobs, workOrderId]);

	React.useEffect(() => {
		if (showModal && workOrderId) {
			fetchData();
		}
	}, [showModal, fetchData, workOrderId]);

	const submit = React.useCallback(() => {
		if (!selectedSubjobId) {
			return;
		}
		const workRequestCode = associatedSubjobs?.find((subjob) => subjob.id === selectedSubjobId)?.jobCode ?? '';
		onSubmit(selectedSubjobId, workRequestCode);
		setSelectedSubjobId(null);
		setSearchText('');
		setAssociatedSubjobs(null);
		closeModal();
	}, [selectedSubjobId, associatedSubjobs, onSubmit, closeModal]);

	const onValueChange = React.useCallback((selectedOption: Nullable<AssociatedSubjobVM>) => () => {
		setSelectedSubjobId(selectedOption?.id ?? null);
	}, []);

	const onClose = React.useCallback(() => {
		setSelectedSubjobId(null);
		setSearchText('');
		setAssociatedSubjobs(null);
		closeModal();
	}, [closeModal]);

	const renderOptions = React.useCallback(() => {
		return (
			filterMap(
				associatedSubjobs ?? [],
				(subjob) => subjob.jobCode.toLowerCase().includes(searchText.toLowerCase()),
				(subjob) => {
					const labelText = subjob.isProject ? 'P' : 'S';
					const classname = subjob.isProject ? '' : 'subjob';

					const label = <div className={styles.label}>
						<Label
							className={classname}
							text={labelText}
						/>
						{subjob.jobCode}
					</div>;

					return (
						<div className={styles.option}>
							<Radio
								checked={selectedSubjobId === subjob.id}
								key={subjob.id}
								label={label}
								onCheck={onValueChange(subjob)}
							/>
						</div>
					);

				}
			)
		);
	}, [associatedSubjobs, onValueChange, searchText, selectedSubjobId]);

	const changeSearchText = React.useCallback((_searchText: string) => {
		setSearchText(_searchText);
	}, []);

	const renderBody = React.useCallback(() => {
		return (
			<div className={styles.options}>
				<SearchInput
					name="searchSubjob"
					onChange={changeSearchText}
					placeholder="Search"
					value={searchText}
				/>
				{
					renderOptions()
				}
			</div>
		);
	}, [changeSearchText, renderOptions, searchText]);

	return (
		<CustomModal
			closeModal={onClose}
			modalStyle="info"
			showModal={showModal}
			size="md"
		>
			<CustomModal.Header closeModal={onClose} title={MODAL_TITLE} />
			<CustomModal.Body padding="none">
				<div className={styles.container}>
					{
						(!associatedSubjobs || !!associatedSubjobs.length) &&
						<Row>
							Select subjob for which you wish to download {forCustomer ? '' : 'Complete '}Field Report {forCustomer ? 'for Customer' : ''}.
						</Row>
					}
					{
						associatedSubjobs === null &&
						<div className={styles.loader}>
							<LoadingIndicator color="orange" />
						</div>
					}
					{
						(!!associatedSubjobs && !associatedSubjobs.length) &&
						<Row>
							No associated subjobs found for this Work Order.
						</Row>
					}
					{
						(!!associatedSubjobs && !!associatedSubjobs.length) &&
						renderBody()
					}
				</div>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={onClose}
					style="secondary"
				/>
				<SubmitButton
					disabled={!selectedSubjobId || !workOrderId}
					label="Export to PDF"
					onClick={submit}
					variant="primary"
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapDispatchToProps() {
	return {
		findSubjobs: WorkOrderActions.findAssociatedSubjobs,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(VirtualReportExportModal);
